"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export function FooterLayout({
  children,
  credit,
}: {
  children: ReactNode;
  credit: ReactNode;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [stacked, setStacked] = useState(false);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    // Measure the actual flex wrap so font size and content determine
    // alignment. The separate credit never affects the first row's wrap.
    const updateAlignment = () => {
      const [attribution, navigation] = Array.from(row.children);
      if (!attribution || !navigation) return;
      setStacked(
        navigation.getBoundingClientRect().top >=
          attribution.getBoundingClientRect().bottom,
      );
    };
    const observer = new ResizeObserver(updateAlignment);
    observer.observe(row);
    updateAlignment();
    return () => observer.disconnect();
  }, []);

  return (
    // Bottom padding keeps every footer link above the fixed scroll control.
    <div
      data-page-gutter
      className={cn(
        "text-muted-foreground container mx-auto space-y-3 pt-10 pb-20 text-base",
        stacked && "text-center",
      )}
    >
      <div
        data-testid="footer-primary-row"
        ref={rowRef}
        className={cn(
          "flex flex-wrap items-center justify-between gap-x-6 gap-y-3",
          stacked && "justify-center",
        )}
      >
        {children}
      </div>
      {credit}
    </div>
  );
}
